import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Tag,
  Space,
  Button,
  Select,
  DatePicker,
  Input,
  Card,
  Statistic,
  Row,
  Col,
  Tooltip,
  Typography,
  Alert,
  Divider,
  Badge
} from 'antd';
import {
  BugOutlined,
  DownloadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { errorLogger, ErrorLogEntry, ErrorStats } from '../../utils/errorLogger';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;
const { Search } = Input;

interface ErrorLogViewerProps {
  visible: boolean;
  onClose: () => void;
}

const ErrorLogViewer: React.FC<ErrorLogViewerProps> = ({ visible, onClose }) => {
  const [logs, setLogs] = useState<ErrorLogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ErrorLogEntry[]>([]);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    component: '',
    search: '',
    dateRange: null as any
  });

  // 加载错误日志
  const loadLogs = () => {
    setLoading(true);
    try {
      const allLogs = errorLogger.getAllLogs();
      const errorStats = errorLogger.getErrorStats();
      setLogs(allLogs);
      setStats(errorStats);
      applyFilters(allLogs);
    } catch (error) {
      console.error('Failed to load error logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // 应用筛选条件
  const applyFilters = (logData: ErrorLogEntry[] = logs) => {
    let filtered = [...logData];

    if (filters.type) {
      filtered = filtered.filter(log => log.type === filters.type);
    }

    if (filters.severity) {
      filtered = filtered.filter(log => log.severity === filters.severity);
    }

    if (filters.component) {
      filtered = filtered.filter(log => log.component === filters.component);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(log =>
        log.message.toLowerCase().includes(searchLower) ||
        log.details?.toLowerCase().includes(searchLower) ||
        log.code?.toString().toLowerCase().includes(searchLower)
      );
    }

    if (filters.dateRange && filters.dateRange.length === 2) {
      const [start, end] = filters.dateRange;
      filtered = filtered.filter(log => {
        const logTime = dayjs(log.timestamp);
        return logTime.isAfter(start) && logTime.isBefore(end);
      });
    }

    setFilteredLogs(filtered);
  };

  // 清除所有日志
  const clearAllLogs = () => {
    Modal.confirm({
      title: '确认清除所有错误日志？',
      content: '此操作不可撤销，将删除所有已记录的错误日志。',
      okText: '确认清除',
      cancelText: '取消',
      okType: 'danger',
      onOk: () => {
        errorLogger.clearLogs();
        loadLogs();
      }
    });
  };

  // 导出日志
  const exportLogs = () => {
    try {
      const exportData = errorLogger.exportLogs();
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `error-logs-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export logs:', error);
    }
  };

  // 获取严重级别颜色
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'red';
      case 'HIGH': return 'orange';
      case 'MEDIUM': return 'yellow';
      case 'LOW': return 'blue';
      default: return 'default';
    }
  };

  // 获取错误类型颜色
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'NETWORK': return 'red';
      case 'API': return 'orange';
      case 'DATA': return 'blue';
      case 'TIMEOUT': return 'purple';
      default: return 'default';
    }
  };

  // 获取严重级别图标
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'HIGH': return <WarningOutlined style={{ color: '#fa8c16' }} />;
      case 'MEDIUM': return <InfoCircleOutlined style={{ color: '#fadb14' }} />;
      case 'LOW': return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      default: return <InfoCircleOutlined />;
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp: string) => (
        <Tooltip title={dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss.SSS')}>
          <Text code>{dayjs(timestamp).format('MM-DD HH:mm:ss')}</Text>
        </Tooltip>
      ),
      sorter: (a: ErrorLogEntry, b: ErrorLogEntry) => 
        dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf(),
      defaultSortOrder: 'descend' as const
    },
    {
      title: '严重级别',
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      render: (severity: string) => (
        <Space>
          {getSeverityIcon(severity)}
          <Tag color={getSeverityColor(severity)}>{severity}</Tag>
        </Space>
      ),
      filters: [
        { text: 'CRITICAL', value: 'CRITICAL' },
        { text: 'HIGH', value: 'HIGH' },
        { text: 'MEDIUM', value: 'MEDIUM' },
        { text: 'LOW', value: 'LOW' }
      ],
      onFilter: (value: any, record: ErrorLogEntry) => record.severity === value
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type}</Tag>
      ),
      filters: [
        { text: 'NETWORK', value: 'NETWORK' },
        { text: 'API', value: 'API' },
        { text: 'DATA', value: 'DATA' },
        { text: 'TIMEOUT', value: 'TIMEOUT' },
        { text: 'UNKNOWN', value: 'UNKNOWN' }
      ],
      onFilter: (value: any, record: ErrorLogEntry) => record.type === value
    },
    {
      title: '组件',
      dataIndex: 'component',
      key: 'component',
      width: 120,
      render: (component: string) => component ? <Tag>{component}</Tag> : '-'
    },
    {
      title: '错误代码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (code: string | number) => code ? <Text code>{code}</Text> : '-'
    },
    {
      title: '错误信息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (message: string, record: ErrorLogEntry) => (
        <Tooltip title={
          <div>
            <div><strong>消息:</strong> {message}</div>
            {record.details && <div><strong>详情:</strong> {record.details}</div>}
            {record.suggestion && <div><strong>建议:</strong> {record.suggestion}</div>}
          </div>
        }>
          <Text>{message}</Text>
        </Tooltip>
      )
    },
    {
      title: '可重试',
      dataIndex: 'retryable',
      key: 'retryable',
      width: 80,
      render: (retryable: boolean) => (
        <Badge 
          status={retryable ? 'success' : 'error'} 
          text={retryable ? '是' : '否'} 
        />
      )
    }
  ];

  useEffect(() => {
    if (visible) {
      loadLogs();
    }
  }, [visible]);

  useEffect(() => {
    applyFilters();
  }, [filters, logs]);

  return (
    <Modal
      title={
        <Space>
          <BugOutlined />
          错误日志查看器
          {stats && (
            <Badge count={stats.total} style={{ backgroundColor: '#52c41a' }} />
          )}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
      destroyOnHidden
    >
      {/* 统计信息 */}
      {stats && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="总错误数"
                value={stats.total}
                prefix={<BugOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="最近1小时"
                value={stats.recentErrors.length}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: stats.recentErrors.length > 10 ? '#cf1322' : '#3f8600' }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="错误率"
                value={stats.errorRate}
                suffix="次/小时"
                prefix={<WarningOutlined />}
                valueStyle={{ color: stats.errorRate > 5 ? '#cf1322' : '#3f8600' }}
              />
            </Col>
            <Col span={6}>
              <Space>
                <Button 
                  type="primary" 
                  icon={<ReloadOutlined />} 
                  onClick={loadLogs}
                  loading={loading}
                >
                  刷新
                </Button>
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={exportLogs}
                >
                  导出
                </Button>
                <Button 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={clearAllLogs}
                >
                  清除
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* 筛选器 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Select
              placeholder="错误类型"
              allowClear
              style={{ width: '100%' }}
              value={filters.type || undefined}
              onChange={(value) => setFilters({ ...filters, type: value || '' })}
            >
              <Option value="NETWORK">NETWORK</Option>
              <Option value="API">API</Option>
              <Option value="DATA">DATA</Option>
              <Option value="TIMEOUT">TIMEOUT</Option>
              <Option value="UNKNOWN">UNKNOWN</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="严重级别"
              allowClear
              style={{ width: '100%' }}
              value={filters.severity || undefined}
              onChange={(value) => setFilters({ ...filters, severity: value || '' })}
            >
              <Option value="CRITICAL">CRITICAL</Option>
              <Option value="HIGH">HIGH</Option>
              <Option value="MEDIUM">MEDIUM</Option>
              <Option value="LOW">LOW</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Input
              placeholder="组件名称"
              allowClear
              value={filters.component}
              onChange={(e) => setFilters({ ...filters, component: e.target.value })}
            />
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              showTime
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            />
          </Col>
          <Col span={6}>
            <Search
              placeholder="搜索错误信息"
              allowClear
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onSearch={(value) => setFilters({ ...filters, search: value })}
            />
          </Col>
        </Row>
      </Card>

      {/* 错误日志表格 */}
      <Table
        columns={columns}
        dataSource={filteredLogs}
        rowKey="id"
        loading={loading}
        pagination={{
          total: filteredLogs.length,
          pageSize: 20,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
        }}
        scroll={{ y: 400 }}
        size="small"
      />
    </Modal>
  );
};

export default ErrorLogViewer;